import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  People as PeopleIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
  Assignment as AssignmentIcon,
  Cloud as CloudIcon,
  Business as BusinessIcon,
  Apps as AppsIcon,
  TrendingUp as TrendingUpIcon,
  Login as LoginIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import { pageVariants, sectionVariants, staggerContainer, staggerItem } from '../animations/variants';

const PRIMARY = '#4698D3';
const SECONDARY = '#D64794';

const statCardStyle = {
  p: 3,
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  border: '1px solid #E5E5E5',
  borderRadius: '12px',
  transition: 'box-shadow 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
};

const AppAdminAnalytics = () => {
  // Platform stats state
  const [platformStats, setPlatformStats] = useState(null);
  const [platformLoading, setPlatformLoading] = useState(true);

  // Login stats state
  const [loginStats, setLoginStats] = useState(null);
  const [loginLoading, setLoginLoading] = useState(true);
  const [loginDays, setLoginDays] = useState(30);

  // Access logs state
  const [accessLogs, setAccessLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPagination, setLogsPagination] = useState({
    page: 0,
    pageSize: 25,
    total: 0,
    totalPages: 0,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [logsFilters, setLogsFilters] = useState({
    startDate: '',
    endDate: '',
    userEmail: '',
    loginType: '',
    customerId: '',
  });

  // Data source selector — synced with URL ?source=
  const [dataSource, setDataSource] = useState(() => {
    if (typeof window === 'undefined') return 'combined';
    const params = new URLSearchParams(window.location.search);
    const v = params.get('source');
    return v === 'saas' || v === 'self_hosted' || v === 'combined' ? v : 'combined';
  });

  // Keep URL in sync when user changes source
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('source', dataSource);
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState(null, '', newUrl);
  }, [dataSource]);

  // Fetch platform stats
  const fetchPlatformStats = useCallback(async () => {
    setPlatformLoading(true);
    try {
      const response = await apiService.getAnalyticsPlatformStats(dataSource);
      if (response.success) {
        setPlatformStats(response.data);
      }
    } catch (err) {
      toast.error('Failed to fetch platform stats');
      console.error('Failed to fetch platform stats:', err);
    } finally {
      setPlatformLoading(false);
    }
  }, [dataSource]);

  // Fetch login stats
  const fetchLoginStats = useCallback(async () => {
    setLoginLoading(true);
    try {
      const response = await apiService.getAnalyticsLoginStats(loginDays || 0, dataSource);
      if (response.success) {
        setLoginStats(response.data);
      }
    } catch (err) {
      toast.error('Failed to fetch login stats');
      console.error('Failed to fetch login stats:', err);
    } finally {
      setLoginLoading(false);
    }
  }, [loginDays, dataSource]);

  // Fetch access logs
  const fetchAccessLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const params = {
        page: logsPagination.page + 1,
        pageSize: logsPagination.pageSize,
        source: dataSource,
      };
      if (logsFilters.startDate) params.startDate = logsFilters.startDate;
      if (logsFilters.endDate) params.endDate = logsFilters.endDate;
      if (logsFilters.userEmail) params.userEmail = logsFilters.userEmail;
      if (logsFilters.loginType) params.loginType = logsFilters.loginType;
      if (logsFilters.customerId) params.customerId = logsFilters.customerId;

      const response = await apiService.getAnalyticsAccessLogs(params);
      if (response.success) {
        setAccessLogs(response.data?.logs || []);
        setLogsPagination(prev => ({
          ...prev,
          total: response.data?.total || 0,
          totalPages: response.data?.totalPages || 0,
        }));
      }
    } catch (err) {
      toast.error('Failed to fetch access logs');
      console.error('Failed to fetch access logs:', err);
    } finally {
      setLogsLoading(false);
    }
  }, [logsPagination.page, logsPagination.pageSize, logsFilters, dataSource]);

  // Initial load
  useEffect(() => {
    fetchPlatformStats();
    fetchLoginStats();
  }, [fetchPlatformStats, fetchLoginStats]);

  useEffect(() => {
    fetchAccessLogs();
  }, [fetchAccessLogs]);

  const handleClearFilters = () => {
    setLogsFilters({
      startDate: '',
      endDate: '',
      userEmail: '',
      loginType: '',
      customerId: '',
    });
    setLogsPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleApplyFilters = () => {
    setLogsPagination(prev => ({ ...prev, page: 0 }));
  };

  const formatNumber = (num) => {
    if (num == null) return '—';
    return num.toLocaleString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatChartDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${months[parseInt(parts[1], 10) - 1]} ${parseInt(parts[2], 10)}`;
    }
    return dateStr;
  };

  const statCards = platformStats ? [
    { label: 'Customers', value: platformStats.totalCustomers, icon: <BusinessIcon />, color: PRIMARY },
    { label: 'Users', value: platformStats.totalUsers, icon: <PeopleIcon />, color: '#27AE60' },
    { label: 'Groups', value: platformStats.totalGroups, icon: <GroupIcon />, color: '#8E44AD' },
    { label: 'Permission Sets', value: platformStats.totalPermissionSets, icon: <SecurityIcon />, color: '#E67E22' },
    { label: 'Assignments', value: platformStats.totalAssignments, icon: <AssignmentIcon />, color: SECONDARY },
    { label: 'AWS Accounts', value: platformStats.totalAwsAccounts, icon: <CloudIcon />, color: '#2C3E50' },
  ] : [];

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>

        {/* Header */}
        <motion.div variants={sectionVariants}>
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#253E66', mb: 0.5 }}>
                Analytics
              </Typography>
              <Typography variant="body2" sx={{ color: '#999' }}>
                Platform-wide statistics and activity across all customer realms
              </Typography>
            </Box>
            <Tooltip title="Refresh all data">
              <IconButton
                onClick={() => { fetchPlatformStats(); fetchLoginStats(); fetchAccessLogs(); }}
                sx={{ color: PRIMARY }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </motion.div>

        {/* Source toggle */}
        <motion.div variants={sectionVariants}>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
              Source:
            </Typography>
            <ToggleButtonGroup
              value={dataSource}
              exclusive
              size="small"
              onChange={(_, value) => { if (value) setDataSource(value); }}
              aria-label="analytics data source"
            >
              <ToggleButton value="saas">SaaS</ToggleButton>
              <ToggleButton value="self_hosted">Self-Hosted</ToggleButton>
              <ToggleButton value="combined">Combined</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </motion.div>

        {/* Platform Stats Cards */}
        <motion.div variants={sectionVariants}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#253E66' }}>
            Platform Overview
          </Typography>
          {platformLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <motion.div variants={staggerContainer} initial="initial" animate="animate">
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {statCards.map((card) => (
                  <Grid size={{ xs: 6, sm: 4, md: 2 }} key={card.label}>
                    <motion.div variants={staggerItem}>
                      <Paper elevation={0} sx={statCardStyle}>
                        <Box sx={{
                          width: 44,
                          height: 44,
                          borderRadius: '10px',
                          backgroundColor: `${card.color}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: card.color,
                          flexShrink: 0,
                        }}>
                          {card.icon}
                        </Box>
                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#253E66', lineHeight: 1.2 }}>
                            {formatNumber(card.value)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#999', fontWeight: 500 }}>
                            {card.label}
                          </Typography>
                        </Box>
                      </Paper>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          )}
        </motion.div>

        {/* Customer Breakdown Table */}
        {platformStats?.customerBreakdown?.length > 0 && (
          <motion.div variants={sectionVariants}>
            <Paper elevation={0} sx={{ mb: 4, border: '1px solid #E5E5E5', borderRadius: '12px', overflow: 'hidden' }}>
              <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #E5E5E5' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#253E66' }}>
                  Customer Breakdown
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#253E66' }}>Customer</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#253E66' }}>Realm</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#253E66' }}>Source</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#253E66' }}>Users</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#253E66' }}>Groups</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#253E66' }}>Permission Sets</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#253E66' }}>Assignments</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#253E66' }}>Active (30d)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {platformStats.customerBreakdown.map((customer) => {
                      const active30d = customer.activeUsers30d || 0;
                      const pctText = customer.users > 0
                        ? `${((active30d / customer.users) * 100).toFixed(1)}%`
                        : '—';
                      return (
                        <TableRow key={customer.customerId} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{customer.customerName}</TableCell>
                          <TableCell>
                            <Chip label={customer.realmName} size="small" sx={{ backgroundColor: '#EBF5FB', color: PRIMARY, fontWeight: 500, fontSize: '0.75rem' }} />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={customer.source === 'self_hosted' ? 'Self-Hosted' : 'SaaS'}
                              size="small"
                              sx={{
                                bgcolor: customer.source === 'self_hosted' ? '#FFF3E0' : '#E3F2FD',
                                color: customer.source === 'self_hosted' ? '#E65100' : '#1565C0',
                                fontWeight: 500,
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">{formatNumber(customer.users)}</TableCell>
                          <TableCell align="right">{formatNumber(customer.groups)}</TableCell>
                          <TableCell align="right">{formatNumber(customer.permissionSets)}</TableCell>
                          <TableCell align="right">{formatNumber(customer.assignments)}</TableCell>
                          <TableCell align="right">
                            {formatNumber(active30d)} <Typography component="span" variant="caption" sx={{ color: '#999' }}>({pctText})</Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </motion.div>
        )}

        {/* Login Analytics */}
        <motion.div variants={sectionVariants}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#253E66' }}>
              Login Analytics
            </Typography>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={loginDays}
                onChange={(e) => setLoginDays(e.target.value)}
                sx={{
                  fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', fontWeight: 500,
                  color: '#666', height: 32,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E5E5' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
                }}
              >
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={60}>Last 60 days</MenuItem>
                <MenuItem value={0}>All time</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loginLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : loginStats ? (
            <>
              {/* Login stat cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper elevation={0} sx={statCardStyle}>
                    <Box sx={{
                      width: 44, height: 44, borderRadius: '10px',
                      backgroundColor: `${PRIMARY}15`, display: 'flex',
                      alignItems: 'center', justifyContent: 'center', color: PRIMARY, flexShrink: 0,
                    }}>
                      <LoginIcon />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#253E66', lineHeight: 1.2 }}>
                        {formatNumber(loginStats.totalLogins)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#999', fontWeight: 500 }}>Total Logins</Typography>
                    </Box>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper elevation={0} sx={statCardStyle}>
                    <Box sx={{
                      width: 44, height: 44, borderRadius: '10px',
                      backgroundColor: '#27AE6015', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', color: '#27AE60', flexShrink: 0,
                    }}>
                      <PeopleIcon />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#253E66', lineHeight: 1.2 }}>
                        {formatNumber(loginStats.uniqueUsers)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#999', fontWeight: 500 }}>Unique Users</Typography>
                    </Box>
                  </Paper>
                </Grid>
                {[
                  { key: 'dau', label: 'DAU', value: loginStats.dau, color: PRIMARY, icon: <TrendingUpIcon /> },
                  { key: 'wau', label: 'WAU', value: loginStats.wau, color: '#27AE60', icon: <PeopleIcon /> },
                  { key: 'mau', label: 'MAU', value: loginStats.mau, color: '#8E44AD', icon: <GroupIcon /> },
                  { key: 'newUsers', label: 'New Users', value: loginStats.newUsers, color: '#E67E22', icon: <TrendingUpIcon /> },
                ].map((tile) => (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }} key={tile.key}>
                    <Paper elevation={0} sx={statCardStyle}>
                      <Box sx={{
                        width: 44, height: 44, borderRadius: '10px',
                        backgroundColor: `${tile.color}15`, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', color: tile.color, flexShrink: 0,
                      }}>
                        {tile.icon}
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#253E66', lineHeight: 1.2 }}>
                          {formatNumber(tile.value || 0)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999', fontWeight: 500 }}>{tile.label}</Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
                {loginStats.loginsByType && Object.entries(loginStats.loginsByType).map(([type, count]) => (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }} key={type}>
                    <Paper elevation={0} sx={statCardStyle}>
                      <Box sx={{
                        width: 44, height: 44, borderRadius: '10px',
                        backgroundColor: `${SECONDARY}15`, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', color: SECONDARY, flexShrink: 0,
                      }}>
                        {type === 'aws_console' ? <CloudIcon /> : <AppsIcon />}
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#253E66', lineHeight: 1.2 }}>
                          {formatNumber(count)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999', fontWeight: 500 }}>
                          {type === 'aws_console' ? 'AWS Console' : type === 'custom_app' ? 'Custom Apps' : type === 'cli' ? 'CLI' : type}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Charts */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Login Trend Chart */}
                {loginStats.loginsByDay?.length > 0 && (
                  <Grid size={{ xs: 12, md: 7 }}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E5E5', borderRadius: '12px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <TrendingUpIcon sx={{ color: PRIMARY, fontSize: 20 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#253E66' }}>
                          Login Trend
                        </Typography>
                      </Box>
                      <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={loginStats.loginsByDay} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                          <defs>
                            <linearGradient id="loginGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={PRIMARY} stopOpacity={0.2} />
                              <stop offset="95%" stopColor={PRIMARY} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                          <XAxis
                            dataKey="date"
                            tickFormatter={formatChartDate}
                            tick={{ fontSize: 11, fill: '#999' }}
                            axisLine={{ stroke: '#E5E5E5' }}
                            tickLine={false}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fill: '#999' }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <RechartsTooltip
                            labelFormatter={formatChartDate}
                            contentStyle={{ borderRadius: 8, border: '1px solid #E5E5E5', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                          />
                          <Area
                            type="monotone"
                            dataKey="count"
                            name="Logins"
                            stroke={PRIMARY}
                            strokeWidth={2}
                            fill="url(#loginGradient)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                )}

                {/* Logins by Customer Chart */}
                {loginStats.loginsByCustomer?.length > 0 && (
                  <Grid size={{ xs: 12, md: 5 }}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E5E5', borderRadius: '12px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <BarChartIcon sx={{ color: SECONDARY, fontSize: 20 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#253E66' }}>
                          Logins by Customer
                        </Typography>
                      </Box>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart
                          data={loginStats.loginsByCustomer}
                          layout="vertical"
                          margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" horizontal={false} />
                          <XAxis
                            type="number"
                            tick={{ fontSize: 11, fill: '#999' }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis
                            dataKey="customerName"
                            type="category"
                            width={120}
                            tick={{ fontSize: 11, fill: '#666' }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <RechartsTooltip
                            contentStyle={{ borderRadius: 8, border: '1px solid #E5E5E5', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                          />
                          <Bar dataKey="count" name="Logins" fill={SECONDARY} radius={[0, 4, 4, 0]} barSize={20} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                )}
              </Grid>

              {/* Rolling Active Users Chart */}
              {loginStats.activeUsersByDay?.length > 0 && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12 }}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E5E5', borderRadius: '12px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <TrendingUpIcon sx={{ color: PRIMARY, fontSize: 20 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#253E66' }}>
                          Rolling Active Users
                        </Typography>
                      </Box>
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={loginStats.activeUsersByDay} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                          <XAxis
                            dataKey="date"
                            tickFormatter={formatChartDate}
                            tick={{ fontSize: 11, fill: '#999' }}
                            axisLine={{ stroke: '#E5E5E5' }}
                            tickLine={false}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fill: '#999' }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <RechartsTooltip
                            labelFormatter={formatChartDate}
                            contentStyle={{ borderRadius: 8, border: '1px solid #E5E5E5', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                          />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Line
                            type="monotone"
                            dataKey="wau"
                            name="WAU"
                            stroke={PRIMARY}
                            strokeWidth={2}
                            dot={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="mau"
                            name="MAU"
                            stroke={SECONDARY}
                            strokeWidth={2}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </>
          ) : (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid #E5E5E5', borderRadius: '12px', mb: 4 }}>
              <Typography variant="body2" sx={{ color: '#999' }}>No login data available</Typography>
            </Paper>
          )}
        </motion.div>

        {/* Access Logs Section */}
        <motion.div variants={sectionVariants}>
          <Divider sx={{ mb: 3 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#253E66' }}>
              Access Logs
              <Typography component="span" variant="body2" sx={{ ml: 1, color: '#999', fontWeight: 400 }}>
                All realms
              </Typography>
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{
                  color: showFilters ? PRIMARY : '#666',
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Filters
              </Button>
              <Tooltip title="Refresh logs">
                <IconButton size="small" onClick={fetchAccessLogs} sx={{ color: PRIMARY }}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Filters */}
          <Collapse in={showFilters}>
            <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #E5E5E5', borderRadius: '12px' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Start Date"
                    type="date"
                    value={logsFilters.startDate}
                    onChange={(e) => setLogsFilters(prev => ({ ...prev, startDate: e.target.value }))}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="End Date"
                    type="date"
                    value={logsFilters.endDate}
                    onChange={(e) => setLogsFilters(prev => ({ ...prev, endDate: e.target.value }))}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="User Email"
                    value={logsFilters.userEmail}
                    onChange={(e) => setLogsFilters(prev => ({ ...prev, userEmail: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Customer</InputLabel>
                    <Select
                      value={logsFilters.customerId}
                      label="Customer"
                      onChange={(e) => setLogsFilters(prev => ({ ...prev, customerId: e.target.value }))}
                    >
                      <MenuItem value="">All</MenuItem>
                      {(platformStats?.customerBreakdown || []).map((c) => (
                        <MenuItem key={c.customerId} value={c.customerId}>{c.customerName}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Login Type</InputLabel>
                    <Select
                      value={logsFilters.loginType}
                      label="Login Type"
                      onChange={(e) => setLogsFilters(prev => ({ ...prev, loginType: e.target.value }))}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="aws_console">AWS Console</MenuItem>
                      <MenuItem value="custom_app">Custom App</MenuItem>
                      <MenuItem value="cli">CLI</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleApplyFilters}
                      sx={{ textTransform: 'none', backgroundColor: PRIMARY }}
                    >
                      Apply
                    </Button>
                    <Button
                      size="small"
                      startIcon={<ClearIcon />}
                      onClick={handleClearFilters}
                      sx={{ textTransform: 'none', color: '#666' }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Collapse>

          {/* Logs Table */}
          <Paper elevation={0} sx={{ border: '1px solid #E5E5E5', borderRadius: '12px', overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#253E66' }}>Timestamp</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#253E66' }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#253E66' }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#253E66' }}>Source</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#253E66' }}>AWS Account</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#253E66' }}>Permission Set / Application</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#253E66' }}>Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logsLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : accessLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: '#999' }}>
                        No access logs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    accessLogs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {formatDate(log.timestamp)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.customerName}
                            size="small"
                            sx={{ backgroundColor: '#EBF5FB', color: PRIMARY, fontWeight: 500, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{log.userEmail}</TableCell>
                        <TableCell>
                          <Chip
                            label={log.source === 'self_hosted' ? 'Self-Hosted' : 'SaaS'}
                            size="small"
                            sx={{
                              bgcolor: log.source === 'self_hosted' ? '#FFF3E0' : '#E3F2FD',
                              color: log.source === 'self_hosted' ? '#E65100' : '#1565C0',
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {log.awsAccountName || log.awsAccountId || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {log.loginType === 'custom_app'
                            ? (log.applicationName || '—')
                            : (log.permissionSet || '—')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.loginType === 'aws_console' ? 'AWS' : log.loginType === 'custom_app' ? 'App' : log.loginType === 'cli' ? 'CLI' : log.loginType}
                            size="small"
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: 500,
                              backgroundColor: log.loginType === 'aws_console' ? '#FFF3E0' : log.loginType === 'cli' ? '#E3F2FD' : '#F3E5F5',
                              color: log.loginType === 'aws_console' ? '#E65100' : log.loginType === 'cli' ? '#1565C0' : '#7B1FA2',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {logsPagination.total > 0 && (
              <TablePagination
                component="div"
                count={logsPagination.total}
                page={logsPagination.page}
                onPageChange={(_, newPage) => setLogsPagination(prev => ({ ...prev, page: newPage }))}
                rowsPerPage={logsPagination.pageSize}
                onRowsPerPageChange={(e) => setLogsPagination(prev => ({ ...prev, pageSize: parseInt(e.target.value, 10), page: 0 }))}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  borderTop: '1px solid #E5E5E5',
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '0.8rem',
                    color: '#666',
                  },
                }}
              />
            )}
          </Paper>
        </motion.div>
      </Box>
    </motion.div>
  );
};

export default AppAdminAnalytics;
